import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { alertByPrefecture, alertLevelType, floodCityType, jmaAlertType } from "../types";

const bucketURL = "https://processed-alert-files.s3.ap-northeast-1.amazonaws.com/"
const heavyRainJsonURL = `${bucketURL}heavy_rain.json`
const floodJsonURL = `${bucketURL}flood.json`
const sampleHeavyRainJsonURL = '/jma-samples/heavy_rain.json'
const sampleFloodJsonURL = '/jma-samples/flood.json'

const getJSON = async (url: string) => {
  try {
    const response = await axios.get(url, {headers: {
      'Cache-Control': 'no-cache'
    }});
    return response.data;
  } catch (error) {
  }
  return null
}

const getJMAFile = async (): Promise<jmaAlertType | null> => {
  const useSampleData = shouldUseSampleData();
  const [heavyRainFile, floodFile] = await Promise.all([
    getJSON(useSampleData ? sampleHeavyRainJsonURL : heavyRainJsonURL),
    getJSON(useSampleData ? sampleFloodJsonURL : floodJsonURL),
  ]);

  if (!heavyRainFile && !floodFile) {
    return null;
  }

  return normalizeJMAFiles(heavyRainFile, floodFile)
}

const useGetJMAFile = () => {
  const [jmaFile, setJMAFile] = useState<jmaAlertType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refreshJMAFile = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const data = await getJMAFile();
      setJMAFile(data);
      setRefreshKey((prev) => prev + 1);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    refreshJMAFile().finally(() => {
      setIsLoading(false);
    });
  }, [refreshJMAFile]);

  return {jmaFile, isLoading, isRefreshing, refreshJMAFile, refreshKey}
}

export default useGetJMAFile;

const shouldUseSampleData = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('jmaSample') === 'true' || localStorage.getItem('jmaAlertSample') === 'true';
};

const normalizeJMAFiles = (heavyRainFile: any, floodFile: any): jmaAlertType => {
  const alertsByPrefectures: Record<string, alertByPrefecture> = {};
  const heavyRainByPrefectures = heavyRainFile?.alerts_by_prefectures || {};
  const floodByCities = floodFile?.alerts_by_cities || {};

  Object.entries(heavyRainByPrefectures).forEach(([prefectureName, value]) => {
    const prefecture = value as any;
    const heavyRainCities = (prefecture.cities || []).map((city: any) => ({
      name: city.name,
      code: city.code,
      level: normalizeLevel(city.level),
      kind: city.kind,
    }));

    alertsByPrefectures[prefectureName] = {
      prefecture: prefecture.prefecture || prefectureName,
      prefecture_code: prefecture.prefecture_code,
      last_updated: prefecture.last_updated || heavyRainFile?.last_updated || '',
      details: prefecture.details,
      district: prefecture.district || getDistrictByPrefecture(prefectureName),
      yahoo_area_name: prefecture.yahoo_area_name,
      max_level: normalizeLevel(prefecture.max_level),
      alerts: heavyRainCities.map((city: any) => ({
        category: '大雨',
        label: city.kind,
        level: city.level,
      })),
      heavy_rain_cities: heavyRainCities,
      flood_cities: [],
    };
  });

  Object.entries(floodByCities).forEach(([prefectureName, value]) => {
    const prefecture = value as any;
    const existing = alertsByPrefectures[prefectureName];
    const floodCities = Object.entries(prefecture.cities || {}).map(([cityCode, cityValue]) => {
      const city = cityValue as any;
      return {
        city_name: city.city_name,
        city_code: cityCode,
        max_level: normalizeLevel(city.max_level),
        rivers: (city.rivers || []).map((river: any) => ({
          event_id: river.event_id,
          river_name: river.river_name,
          level: normalizeLevel(river.level),
          kind: river.kind,
          is_cleared: !!river.is_cleared || river.level === 0,
          last_updated: river.last_updated,
        })),
      } as floodCityType;
    });
    const floodAlerts = floodCities.flatMap(city => city.rivers).map(river => ({
      category: '氾濫' as const,
      label: river.kind,
      level: river.level,
      is_cleared: river.is_cleared,
    }));

    alertsByPrefectures[prefectureName] = {
      prefecture: existing?.prefecture || prefectureName,
      prefecture_code: existing?.prefecture_code || prefecture.prefecture_code,
      last_updated: getLatestDateString(existing?.last_updated, floodFile?.last_updated),
      details: existing?.details,
      district: existing?.district || getDistrictByPrefecture(prefectureName),
      yahoo_area_name: existing?.yahoo_area_name,
      max_level: Math.max(existing?.max_level || 0, normalizeLevel(prefecture.max_level)) as alertLevelType,
      alerts: [...(existing?.alerts || []), ...floodAlerts],
      heavy_rain_cities: existing?.heavy_rain_cities || [],
      flood_cities: floodCities,
    };
  });

  return {
    last_updated: getLatestDateString(heavyRainFile?.last_updated, floodFile?.last_updated),
    alerts_by_prefectures: alertsByPrefectures,
  };
};

const normalizeLevel = (level: number): alertLevelType => {
  if (level === 5 || level === 4 || level === 3 || level === 2) {
    return level;
  }
  return 0;
};

const getLatestDateString = (dateA?: string, dateB?: string) => {
  if (!dateA) {
    return dateB || '';
  }
  if (!dateB) {
    return dateA;
  }
  return new Date(dateA).getTime() >= new Date(dateB).getTime() ? dateA : dateB;
};

const getDistrictByPrefecture = (prefectureName: string) => {
  const districtByPrefecture: Record<string, string> = {
    北海道: '北海道地方',
    青森県: '東北地方', 岩手県: '東北地方', 宮城県: '東北地方', 秋田県: '東北地方', 山形県: '東北地方', 福島県: '東北地方',
    茨城県: '関東甲信地方', 栃木県: '関東甲信地方', 群馬県: '関東甲信地方', 埼玉県: '関東甲信地方', 千葉県: '関東甲信地方', 東京都: '関東甲信地方', 神奈川県: '関東甲信地方', 山梨県: '関東甲信地方', 長野県: '関東甲信地方',
    岐阜県: '東海地方', 静岡県: '東海地方', 愛知県: '東海地方', 三重県: '東海地方',
    新潟県: '北陸地方', 富山県: '北陸地方', 石川県: '北陸地方', 福井県: '北陸地方',
    滋賀県: '近畿地方', 京都府: '近畿地方', 大阪府: '近畿地方', 兵庫県: '近畿地方', 奈良県: '近畿地方', 和歌山県: '近畿地方',
    鳥取県: '中国地方', 島根県: '中国地方', 岡山県: '中国地方', 広島県: '中国地方', 山口県: '中国地方',
    徳島県: '四国地方', 香川県: '四国地方', 愛媛県: '四国地方', 高知県: '四国地方',
    福岡県: '九州地方', 佐賀県: '九州地方', 長崎県: '九州地方', 熊本県: '九州地方', 大分県: '九州地方', 宮崎県: '九州地方', 鹿児島県: '九州地方',
    沖縄県: '沖縄地方',
  };
  return districtByPrefecture[prefectureName] || 'その他';
};